import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Input, Button, RadioButton } from '../../shared';
import styles from '../Main.module.scss';
import {
    emailLabel,
    emailPlaceholder,
    nameLabel,
    namePlaceholder,
    organizationLabel,
    organizationPlaceholder,
    phoneLabel,
    phonePlaceholder,
    radioButtons,
    rolesTitle,
    sendAndGoToDemo,
    sendDetails,
    someThingWentWrong,
    subtitle,
    title,
    welcome
} from './textConsts';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
    ADMIN_API,
    emailIsRequiredText,
    emailKey,
    groupNameKey,
    invalidPhoneText,
    nameIsRequiredText,
    nameKey,
    phoneKey,
    phoneRegExp,
    roleIsRequiredText,
    roleKey
} from '../../shared/consts';
import PropTypes from 'prop-types';
import useMediaQuery from '../../shared/hooks/useMediaQuery';
import { isMobileQuery } from '../../shared/MediaQuery/mediaQueryUtils';

const schema = yup
    .object({
        [emailKey]: yup.string().email().required(emailIsRequiredText),
        [nameKey]: yup.string().required(nameIsRequiredText),
        [roleKey]: yup.string().required(roleIsRequiredText).default(radioButtons[0]),
        [groupNameKey]: yup.string(),
        [phoneKey]: yup.string().test(phoneKey, invalidPhoneText, (value) => {
            if (!value) {
                return true;
            }
            return phoneRegExp.test(value);
        })
    })
    .required();

const ContactUs = ({ setUserEmail, setLoading, goToDemo }) => {
    const isMobile = useMediaQuery(isMobileQuery);
    const {
        register,
        formState: { errors },
        handleSubmit,
        setValue,
        watch,
        control
    } = useForm({ resolver: yupResolver(schema) });
    const handleContactUs = async (data, e) => {
        const shouldGoToDemo = e.target.name === goToDemo;
        data['redirect_to_demo'] = shouldGoToDemo;
        setLoading(true);
        try {
            await axios.post(`${ADMIN_API}/contact_us_lead`, data);
            shouldGoToDemo ? goToDemo(data.email) : setUserEmail(data.email);
            setLoading(false);
        } catch (e) {
            setLoading(false);
            toast.error(e?.response?.data?.message || someThingWentWrong);
        }
    };

    return (
        <>
            <div className={styles.contact_us_form_header}>
                <div className={styles.title}>{welcome}</div>
                <div className={styles.title}>{title}</div>
                <div className={styles.subtitle}>{subtitle}</div>
            </div>
            <form onSubmit={handleSubmit(handleContactUs)}>
                <div className={styles.contact_us_form}>
                    <Controller
                        name={nameKey}
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <Input
                                type="text"
                                label={nameLabel}
                                errors={errors[nameKey]}
                                placeholder={namePlaceholder}
                                register={register(nameKey).ref}
                                onChange={onChange}
                                value={value}
                            />
                        )}
                        z
                    />
                    <Controller
                        name={emailKey}
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <Input
                                type="text"
                                label={emailLabel}
                                errors={errors?.email}
                                placeholder={emailPlaceholder}
                                register={register(emailKey).ref}
                                onChange={onChange}
                                value={value}
                            />
                        )}
                    />
                    <div className={styles.roles_title}>{rolesTitle}</div>
                    <div className={styles.radio_buttons_container}>
                        {radioButtons.map((radioButton) => (
                            <RadioButton
                                id={radioButton}
                                key={radioButton}
                                value={radioButton}
                                label={radioButton}
                                handleClick={() => setValue(roleKey, radioButton)}
                                currentValue={watch(roleKey) || radioButtons[0]}
                                register={register(roleKey)}
                            />
                        ))}
                    </div>
                    <Controller
                        name={groupNameKey}
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <Input
                                type="text"
                                label={organizationLabel}
                                errors={errors[groupNameKey]}
                                placeholder={organizationPlaceholder}
                                register={register(groupNameKey).ref}
                                onChange={onChange}
                                value={value}
                            />
                        )}
                    />
                    <Controller
                        name={phoneKey}
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <Input
                                type="text"
                                label={phoneLabel}
                                errors={errors[phoneKey]}
                                placeholder={phonePlaceholder}
                                register={register(phoneKey).ref}
                                onChange={onChange}
                                value={value}
                            />
                        )}
                    />
                </div>
                <div className={styles.contact_us_buttons_container}>
                    <Button wide color="yellow" type="submit" noUppercase>
                        {isMobile ? sendDetails : sendAndGoToDemo}
                    </Button>
                    {!isMobile && (
                        <Button color="white-outlined" type="submit" wide noUppercase>
                            {sendDetails}
                        </Button>
                    )}
                </div>
            </form>
        </>
    );
};

ContactUs.propTypes = {
    goToDemo: PropTypes.func,
    setUserEmail: PropTypes.func,
    setLoading: PropTypes.func
};
export default ContactUs;
